import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import { apiRequest } from '@/shared/api/client';
import type { LoginResponse } from '@/shared/api/types';

/* Face ID / Touch ID / Windows Hello — passwordless login (Р36), admin only. Every call goes
   through the same `apiRequest` the password-login flow uses: register-options/register/
   credentials require the caller to already be logged in (a Bearer token attaches itself the
   same way it does everywhere else in the app); login-options/login are public — apiRequest
   simply has no token to attach yet, same as adminLogin()/clientLogin(). */

export interface WebauthnCredentialSummary {
  id: string;
  name: string;
  created_at: string;
  last_used_at: string | null;
}

interface WebauthnLoginOptionsResponse {
  challenge_id: string;
  options: PublicKeyCredentialRequestOptionsJSON;
}

export const webauthnApi = {
  registerOptions: (): Promise<PublicKeyCredentialCreationOptionsJSON> =>
    apiRequest<PublicKeyCredentialCreationOptionsJSON>('/v1/auth/webauthn/register-options', {
      method: 'POST',
    }),

  register: (credential: RegistrationResponseJSON): Promise<void> =>
    apiRequest<void>('/v1/auth/webauthn/register', { method: 'POST', json: credential }),

  loginOptions: (): Promise<WebauthnLoginOptionsResponse> =>
    apiRequest<WebauthnLoginOptionsResponse>('/v1/auth/webauthn/login-options', {
      method: 'POST',
    }),

  login: (challengeId: string, credential: AuthenticationResponseJSON): Promise<LoginResponse> =>
    apiRequest<LoginResponse>('/v1/auth/webauthn/login', {
      method: 'POST',
      json: { challenge_id: challengeId, credential },
    }),

  listCredentials: (): Promise<WebauthnCredentialSummary[]> =>
    apiRequest<WebauthnCredentialSummary[]>('/v1/auth/webauthn/credentials'),

  deleteCredential: (id: string): Promise<void> =>
    apiRequest<void>(`/v1/auth/webauthn/credentials/${id}`, { method: 'DELETE' }),
};
